import { Button, HStack, Text } from '@chakra-ui/react';
import { Table as TableType } from '@tanstack/react-table';
import * as _ from 'lodash';
import React from 'react';
import { v4 as uuidV4 } from 'uuid';

import { Util } from '@finley-technologies/finley-types';

import { ReceivablePaginationMetadataGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { Icon as FaIcon } from 'packages/common-frontend/components/icons';

const createNumberArray = ({
  arrayLength,
  offset,
}: {
  arrayLength: number;
  offset: number;
}): Array<string | number> => {
  const pageNumberArray: Array<string | number> = Array.from(
    Array(arrayLength).keys(),
  ).map((i) => i + 1 + offset);

  return pageNumberArray;
};

const scrollToTopOfTable = () => {
  document
    .getElementById('receivables-dashboard-main-table')
    ?.scroll({ top: 0 });
};

type ReceivablesTablePaginationControlProps = {
  table: TableType<any>;
  paginationMetadata: ReceivablePaginationMetadataGQLType;
  refetchReceivables: ({
    queryPageNumber,
    queryPageBoundaryReceivableIds,
  }: {
    queryPageNumber: number;
    queryPageBoundaryReceivableIds: Util.Maybe<string>[];
  }) => void;
};
export const ReceivablesTablePaginationControl = ({
  table,
  paginationMetadata,
  refetchReceivables,
}: ReceivablesTablePaginationControlProps) => {
  const pageOffset = (paginationMetadata.queryPageNumber - 1) * 10;
  const tablePageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1;
  const tablePages = createNumberArray({
    arrayLength: tablePageCount,
    offset: pageOffset,
  });

  return (
    <HStack w="100%" spacing="8px" justifyContent="center" p="32px 0">
      <Button
        leftIcon={<FaIcon icon="chevron-left" />}
        colorScheme="aurora"
        variant="ghost"
        isDisabled={
          tablePageCount === 0 ||
          (!table.getCanPreviousPage() &&
            paginationMetadata.queryPageNumber === 1)
        }
        onClick={() => {
          if (table.getCanPreviousPage()) {
            scrollToTopOfTable();
            table.previousPage();
          } else if (paginationMetadata.queryPageNumber > 1) {
            refetchReceivables({
              queryPageNumber: paginationMetadata.queryPageNumber - 1,
              queryPageBoundaryReceivableIds:
                paginationMetadata.queryPageBoundaryReceivableIds,
            });
            table.setPageIndex(9);
          }
        }}
      >
        Prev
      </Button>
      {tablePages.length > 0 &&
        _.map(tablePages, (tablePageNumber) =>
          _.isNumber(tablePageNumber) ? (
            <Button
              colorScheme={
                currentPage + pageOffset === tablePageNumber
                  ? undefined
                  : 'aurora'
              }
              variant={
                currentPage + pageOffset === tablePageNumber ? 'solid' : 'ghost'
              }
              onClick={() => {
                scrollToTopOfTable();
                table.setPageIndex(tablePageNumber - pageOffset - 1);
              }}
              key={_.isNumber(tablePageNumber) ? tablePageNumber : uuidV4()}
            >
              {tablePageNumber}
            </Button>
          ) : (
            <Text key={uuidV4()}>...</Text>
          ),
        )}
      {tablePages.length === 0 && (
        <Button isDisabled variant="ghost">
          ...
        </Button>
      )}
      <Button
        rightIcon={<FaIcon icon="chevron-right" />}
        colorScheme="aurora"
        variant="ghost"
        onClick={() => {
          if (table.getCanNextPage()) {
            scrollToTopOfTable();
            table.nextPage();
          } else if (paginationMetadata.hasNextPage) {
            refetchReceivables({
              queryPageNumber: paginationMetadata.queryPageNumber + 1,
              queryPageBoundaryReceivableIds:
                paginationMetadata.queryPageBoundaryReceivableIds,
            });
            table.setPageIndex(0);
          }
        }}
        isDisabled={
          tablePageCount === 0 ||
          (!table.getCanNextPage() && !paginationMetadata.hasNextPage)
        }
      >
        Next
      </Button>
    </HStack>
  );
};
